import React, { useMemo } from 'react';
import { Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { ChargingStation } from '../../types';
import { chargerIcon } from './MapUtils';
import { getPointPosition } from './markerIconCache';
import { useTranslation } from 'react-i18next';
import { Zap, Navigation2 } from 'lucide-react';
import { openExternalNavigation } from '../../services/navigationService';
import L from 'leaflet';

interface ClusteredChargerMarkersProps {
  chargers: ChargingStation[];
  onInteract: () => void;
  isMobile: boolean;
}

/**
 * `chargerIcon(station)` ignorerer `station` helt (se MapUtils) — alle enkelt-ladere
 * får samme nål. Bygget ÉN gang: lagd inline i `.map()` ga et nytt L.divIcon per
 * render, og react-leaflet kaller `setIcon` når identiteten endres. Se
 * markerIconCache.ts for hele kjeden.
 */
const CHARGER_ICON = chargerIcon(null);

const ClusteredChargerMarkers: React.FC<ClusteredChargerMarkersProps> = ({ chargers, onInteract }) => {
  const { t } = useTranslation();

  // Ett delt handler-objekt for alle ladere: klikket bruker ikke `c` til noe.
  // Som inline-objektliteral fyrte @react-leaflet/core sin `useEventHandlers`
  // off()+on() på hver markør ved hver render.
  //
  // MÅ ligge før den tidlige returen under — en hook bak en betingelse endrer
  // antall hooks mellom rendringer så snart lista går fra tom til ikke-tom.
  const sharedHandlers = useMemo(
    () => ({ click: (e: L.LeafletMouseEvent) => { L.DomEvent.stop(e); onInteract(); } }),
    [onInteract],
  );

  if (chargers.length === 0) return null;

  return (
    <MarkerClusterGroup
      chunkedLoading
      maxClusterRadius={45}
      showCoverageOnHover={false}
      iconCreateFunction={(cluster: any) => chargerIcon(null, cluster.getChildCount())}
    >
      {chargers.map((c) => (
        <Marker
          key={c.id}
          position={getPointPosition(c)}
          icon={CHARGER_ICON}
          eventHandlers={sharedHandlers}
        >
          <Popup className="custom-popup">
            <div className="p-3 space-y-2 min-w-[200px]">
              <div className="flex items-center gap-2 text-yellow-600 font-black text-3xs uppercase tracking-widest">
                <Zap size={12} fill="currentColor" /> {t('map.charger.station')}
              </div>
              <div className="text-stone-900 font-black text-xs leading-tight">{c.name}</div>
              <div className="flex flex-col gap-1 text-2xs text-[var(--text-muted)]">
                <span>{t('map.charger.points', { count: c.chargingPoints })}</span>
                {c.connectors.length > 0 && <span className="text-3xs opacity-70 truncate">{c.connectors.join(', ')}</span>}
              </div>
              <button onClick={(e) => { e.stopPropagation(); openExternalNavigation(c.location.latitude, c.location.longitude); }} className="mt-2 w-full bg-yellow-600 text-white py-1.5 rounded-lg text-3xs font-black uppercase flex items-center justify-center gap-2">
                <Navigation2 size={12} className="rotate-45" /> {t('map.charger.navigate')}
              </button>
            </div>
          </Popup>
        </Marker>
      ))}
    </MarkerClusterGroup>
  );
};

export default ClusteredChargerMarkers;
